import { useCallback, useEffect, useRef, useState } from 'react';
import { getAlarmRuleErrorMessage } from '../filterParameterMapping';

export const ALARM_PAGE_SIZE = 100;

export const useAlarmEventLazyRows = (cacheKey, onLoadOccurrences, onLoadOccurrenceEvents, onLoadError) => {
  const [branches, setBranches] = useState({});
  const cache = useRef({});
  const generation = useRef(0);
  const active = useRef(false);
  const currentCacheKey = useRef(cacheKey);

  useEffect(() => {
    currentCacheKey.current = cacheKey;
    generation.current += 1;
    active.current = true;
    cache.current = {};
    setBranches({});
    return () => {
      active.current = false;
      generation.current += 1;
    };
  }, [cacheKey]);

  const loadPage = useCallback(async (branch, retry = false) => {
    if (!active.current || cacheKey == null || currentCacheKey.current !== cacheKey) return;
    const previous = cache.current[branch.rowId] || { items: [], skip: 0, hasMore: true };
    if (previous.loading || !previous.hasMore || (previous.error && !retry)) return;
    if (branch.total != null && previous.skip >= branch.total) return;
    const requestGeneration = generation.current;
    const publish = value => {
      cache.current = { ...cache.current, [branch.rowId]: value };
      setBranches(cache.current);
    };
    publish({ ...previous, loading: true, error: null });
    try {
      const params = { count: ALARM_PAGE_SIZE, skip: previous.skip, alarmId: branch.alarmId };
      const response = branch.rowType === 'consist'
        ? await onLoadOccurrences({ ...params, consist: branch.queryConsist ?? branch.consist })
        : await onLoadOccurrenceEvents({ ...params, occurrenceIndex: branch.occurrenceIndex });
      if (!active.current || requestGeneration !== generation.current) return;
      if (!Array.isArray(response?.items)) throw new Error('Invalid alarm page response');
      const identity = item => String(branch.rowType === 'consist' ? item.occurrenceIndex : item.id);
      const items = new Map(previous.items.map(item => [identity(item), item]));
      response.items.forEach(item => items.set(identity(item), item));
      const skip = previous.skip + response.items.length;
      publish({
        items: Array.from(items.values()),
        skip,
        loading: false,
        error: null,
        initialized: true,
        hasMore: response.items.length === ALARM_PAGE_SIZE && (branch.total == null || skip < branch.total),
      });
    } catch (error) {
      if (!active.current || requestGeneration !== generation.current) return;
      const message = getAlarmRuleErrorMessage(error, 'Failed to load rows.');
      console.error('Failed to load alarm tree rows:', error);
      publish({ ...previous, loading: false, error: message });
      onLoadError?.(message);
    }
  }, [cacheKey, onLoadOccurrences, onLoadOccurrenceEvents, onLoadError]);

  return { branches, loadPage };
};